// communityData.js — Seed products for the Discover hub
// Uses one canonical official EndProduct manifest that is also consumed by the
// Firestore seed script. This keeps the web fallback catalog aligned with the
// documents Discover clients read from Firestore.

import officialEndProducts from './data/officialEndProducts.json';

const makeProduct = (overrides) => ({
  trustTier: 'official',
  reviewStatus: 'verified',
  pricing: 'free',
  securityReviewStatus: 'approved',
  moderationStatus: 'clean',
  targets: ['mchatai-plus', 'mchatai-ios'],
  publisher: { publisherId: 'mchatai-labs', displayName: 'mChatAI Labs', handle: 'mchatai-labs', tier: 'official' },
  publisherName: 'mChatAI Labs',
  installs: 0,
  downloadCount: 0,
  installSuccessRate: 1.0,
  averageRating: 0,
  ratingCount: 0,
  heartCount: 0,
  latestVersion: '1.0.0',
  version: '1.0.0',
  updatedAt: '2026-04-01T00:00:00Z',
  publishedAt: '2026-04-01T00:00:00Z',
  tags: [],
  comments: [],
  permissions: [],
  ...overrides,
});

const defaultPublisher = {
  publisherId: 'mchatai-labs',
  displayName: 'mChatAI Labs',
  handle: 'mchatai-labs',
  tier: 'official'
};

const officialSeedPackages = officialEndProducts.map((product) => makeProduct({
  packageId: product.packageId ?? product.id,
  type: product.type ?? product.contentCategory,
  publisher: product.publisher ?? defaultPublisher,
  installs: product.installs ?? product.downloadCount ?? 0,
  downloadCount: product.downloadCount ?? product.installs ?? 0,
  latestVersion: product.latestVersion ?? product.version ?? '1.0.0',
  version: product.version ?? product.latestVersion ?? '1.0.0',
  ...product
}));

export const communitySeed = {
  packages: officialSeedPackages
};

export function summarizePackage(pkg) {
  return {
    packageId: pkg.packageId,
    id: pkg.id || pkg.packageId,
    slug: pkg.slug,
    name: pkg.name,
    type: pkg.type,
    contentCategory: pkg.contentCategory || pkg.type,
    category: pkg.category,
    topics: Array.isArray(pkg.topics) ? pkg.topics : [],
    summary: pkg.summary,
    publisher: pkg.publisher,
    publisherName: pkg.publisherName || pkg.publisher?.displayName,
    trustTier: pkg.trustTier,
    reviewStatus: pkg.reviewStatus,
    pricing: pkg.pricing || 'free',
    targets: pkg.targets,
    tags: pkg.tags,
    installs: pkg.installs || pkg.downloadCount || 0,
    downloadCount: pkg.downloadCount || pkg.installs || 0,
    heartCount: pkg.heartCount || 0,
    installSuccessRate: pkg.installSuccessRate || 1.0,
    averageRating: pkg.averageRating || 0,
    ratingCount: pkg.ratingCount || 0,
    latestVersion: pkg.latestVersion || pkg.version || '1.0.0',
    version: pkg.version || pkg.latestVersion || '1.0.0',
    updatedAt: pkg.updatedAt,
    previewURL: pkg.previewURL,
    bundleURL: pkg.bundleURL,
    commentCount: pkg.comments?.length ?? 0
  };
}

export function getPackages(filters = {}) {
  const query = (filters.query ?? '').trim().toLowerCase();
  const category = (filters.category ?? '').trim().toLowerCase();
  const target = (filters.target ?? '').trim().toLowerCase();
  const trust = (filters.trust ?? '').trim().toLowerCase();

  let packages = communitySeed.packages.slice();
  if (query) {
    packages = packages.filter((pkg) =>
      [pkg.name, pkg.summary, pkg.description, pkg.publisherName, pkg.publisher?.displayName, ...(pkg.tags ?? [])]
        .join(' ').toLowerCase().includes(query)
    );
  }
  if (category) {
    packages = packages.filter((pkg) =>
      (pkg.contentCategory ?? pkg.type ?? '').toLowerCase() === category
    );
  }
  if (target) {
    packages = packages.filter((pkg) => pkg.targets.some((t) => t.toLowerCase() === target));
  }
  if (trust) {
    packages = packages.filter((pkg) => pkg.trustTier.toLowerCase() === trust);
  }
  return packages.map(summarizePackage);
}

export function getPackageById(id) {
  return communitySeed.packages.find((pkg) =>
    pkg.packageId === id || pkg.slug === id || pkg.id === id
  ) ?? null;
}

export function buildFallbackInstallIntent(pkg, version, product) {
  return {
    delivery: {
      uri: `com.sevenhillsstudio.mchatai://community/install?id=${encodeURIComponent(pkg.id || pkg.packageId)}`
    }
  };
}
